import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, EntityList } from '@aimsos/ui';

import { CLAIM_STATUS_VARIANTS } from '../../../constants/knowledge.js';

import { MapPin, User } from 'lucide-react';

/**
 * One claim of a Sandbox group (CT-5718), on `EntityList`'s slots, as in the
 * prototype (no icon per claim — the group's header names the source):
 *
 *   label · value        -> title
 *   category             -> a chip beside the title (primaryMeta)
 *   status               -> status badge (To confirm, Confirmed, Conflict, Inferred)
 *   quote                -> description
 *   speaker, file · time -> secondaryMeta (the file behind a location pin)
 *   Confirm              -> secondary action; Send for confirmation (or "Sent") a ghost one
 *   in conflict          -> Compare instead
 *
 * The actions — Compare included — show only where the API says the viewer may
 * confirm (`canConfirm`).
 */
const SandboxClaimRow = memo(function SandboxClaimRow({ claim, busy = false, onAction, onCompare }) {
  const { t } = useTranslation('unified-profiles');

  const handleConfirm = useCallback(() => onAction(claim, 'confirm'), [onAction, claim]);
  const handleSend = useCallback(() => onAction(claim, 'send'), [onAction, claim]);
  const handleCompare = useCallback(() => onCompare(claim), [onCompare, claim]);

  const view = useMemo(() => {
    const where = [claim.file, claim.timestamp].filter(Boolean).join(' · ');
    const base = {
      title: `${claim.label} · ${claim.value}`,
      primaryMeta: claim.category
        ? [
            {
              id: 'category',
              label: (
                <span data-testid="sandbox-claim-category">
                  <Badge variant="neutral" size="small">
                    {t(`knowledge.sandbox.categories.${claim.category}`)}
                  </Badge>
                </span>
              ),
            },
          ]
        : undefined,
      status: { label: t(`knowledge.sandbox.status.${claim.status}`), variant: CLAIM_STATUS_VARIANTS[claim.status] ?? 'neutral' },
      description: claim.quote ? `“${claim.quote}”` : undefined,
      secondaryMeta: [
        claim.speaker ? { icon: User, label: claim.speaker } : null,
        where ? { icon: MapPin, label: where } : null,
      ].filter(Boolean),
    };
    if (!claim.canConfirm) return base;
    if (claim.status === 'conflict') {
      return { ...base, secondaryCta: { label: t('knowledge.sandbox.actions.compare'), onClick: handleCompare } };
    }
    if (claim.status !== 'to_confirm') return base;
    return {
      ...base,
      primaryCta: { label: t('knowledge.sandbox.actions.confirm'), variant: 'secondary', onClick: handleConfirm, disabled: busy },
      secondaryCta: claim.sent
        ? { label: t('knowledge.sandbox.actions.sent'), variant: 'ghost', disabled: true }
        : { label: t('knowledge.sandbox.actions.send'), variant: 'ghost', onClick: handleSend, disabled: busy },
    };
  }, [claim, busy, handleConfirm, handleSend, handleCompare, t]);

  return (
    <div data-testid="sandbox-claim" data-claim-id={claim.id} data-status={claim.status} className="py-3 last:pb-0">
      <EntityList {...view} />
    </div>
  );
});

export default SandboxClaimRow;
